/**
 * Hook that aggregates actual feed data by month for planned-vs-actual comparison.
 *
 * Pulls: feed events (consumption), yield events (harvests), feed purchases,
 * feed stock (current inventory), and actual growth rates from field readings.
 *
 * Fetches ALL events (no year filter) so we can show historical data.
 */

import { useEventsForFields, useFields } from "model";
import { YIELD_TYPES } from "model/commonTypes";
import { getAllFieldsForAnimalGroupIdFromArray } from "model/fieldHelpers";
import { useCurrentFarmAnimalGroups } from "model/useAnimalGroupQueries";
import { useCurrentFarm } from "model/useFarmQueries";
import { useFeedEvents } from "model/useFeedEventQueries";
import { useFeedPurchaseEvents } from "model/useFeedPurchaseEventQueries";
import { useFeedStock } from "model/useFeedStockQueries";
import { useStockClassesByIds } from "model/useStockClassQueries";
import { useYieldEvents } from "model/useYieldEventQueries";
import type { GrassYieldDetails } from "model/yieldEventTypes";
import { SOLD_OR_KEPT } from "model/yieldEventTypes";
import { useMemo } from "react";
import { DEFAULT_GRAZING_DAYS } from "./constants";
import { getGrowthRatesKgM2DayFromReadings } from "./growthrate";

/** Monthly actual data for one month */
export interface MonthlyActual {
  month: number;
  /** Feed consumed from store (kg DM) */
  feedConsumedKg: number;
  /** Feed purchased (kg) */
  feedPurchasedKg: number;
  /** Forage harvested and kept (kg DM) */
  forageHarvestedKeptKg: number;
  /** Forage harvested and sold (kg DM) */
  forageHarvestedSoldKg: number;
}

export interface ActualFeedData {
  monthlyActuals: MonthlyActual[];
  /** Actual growth rates by month (kg DM/ha/day), derived from field readings */
  actualGrowthRatesKgHaDay: number[];
  /** Actual demand (kg DM/ha/day), derived from current stock class data */
  actualDemandKgHaDay: number;
  currentStockKg: Record<string, number>;
  totalCurrentStockKg: number;
  isLoading: boolean;
  currentMonth: number;
}

function toKg(amount: number, unit: string): number {
  if (unit === "TONNE") return amount * 1000;
  return amount;
}

export function useActualFeedData(): ActualFeedData {
  const { data: farm } = useCurrentFarm();
  const farmGuid = farm?.guid;
  const currentMonth = new Date().getMonth();

  // Fetch ALL events — no year filter, so we see historical data
  const { data: feedEvents = [], isLoading: feedLoading } =
    useFeedEvents(farmGuid);
  const { data: purchaseEvents = [], isLoading: purchaseLoading } =
    useFeedPurchaseEvents(farmGuid);
  const { data: yieldEvents = [], isLoading: yieldLoading } =
    useYieldEvents(farmGuid);
  const { data: feedStockData, isLoading: stockLoading } =
    useFeedStock(farmGuid);

  // Get animal groups and stock classes for actual demand
  const { data: animalGroups = [] } = useCurrentFarmAnimalGroups();
  const allStockClassIds = useMemo(
    () => animalGroups.flatMap((ag) => ag.stockClasses),
    [animalGroups]
  );
  const { data: stockClasses = [] } = useStockClassesByIds(allStockClassIds);

  // Get field readings for actual growth rates
  const { data: allFields = [] } = useFields();
  const fieldGuids = useMemo(() => allFields.map((f) => f.guid), [allFields]);
  const readingsByFieldGuid = useEventsForFields(fieldGuids);

  // Compute actual growth rates from field readings
  const actualGrowthRatesKgHaDay = useMemo(() => {
    // Convert Record to Map as required by getGrowthRatesKgM2DayFromReadings
    const readingsMap = new Map(Object.entries(readingsByFieldGuid));
    const growthRatesByField = getGrowthRatesKgM2DayFromReadings(readingsMap);

    if (growthRatesByField.size === 0) {
      return Array.from({ length: 12 }, () => 0);
    }

    // Average across all fields, convert m² to ha
    const sumRates = new Array(12).fill(0);
    for (const rates of growthRatesByField.values()) {
      for (let i = 0; i < 12; i++) {
        sumRates[i] += rates[i];
      }
    }
    return sumRates.map((v) => (v * 10000) / growthRatesByField.size);
  }, [readingsByFieldGuid]);

  const monthlyActuals = useMemo(() => {
    const actuals: MonthlyActual[] = Array.from({ length: 12 }, (_, month) => ({
      month,
      feedConsumedKg: 0,
      feedPurchasedKg: 0,
      forageHarvestedKeptKg: 0,
      forageHarvestedSoldKg: 0,
    }));

    for (const event of feedEvents) {
      const month = new Date(event.feedEventDate).getMonth();
      for (const feed of event.feed) {
        actuals[month].feedConsumedKg += toKg(feed.amount, feed.unitType);
      }
    }

    for (const event of purchaseEvents) {
      const month = new Date(event.feedPurchaseEventDate).getMonth();
      for (const purchase of event.feedPurchases) {
        actuals[month].feedPurchasedKg += toKg(
          purchase.amount,
          purchase.unitType
        );
      }
    }

    for (const event of yieldEvents) {
      const details = event.yieldDetails;
      if (details.yieldType !== YIELD_TYPES.GRASS) continue;
      const grassDetails: GrassYieldDetails = details;
      const month = new Date(event.yieldEventDate).getMonth();

      for (const ya of grassDetails.yieldAmounts) {
        const kg = toKg(ya.amount, ya.unitType);
        if (ya.soldOrKept === SOLD_OR_KEPT.KEPT) {
          actuals[month].forageHarvestedKeptKg += kg;
        } else {
          actuals[month].forageHarvestedSoldKg += kg;
        }
      }
    }

    return actuals;
  }, [feedEvents, purchaseEvents, yieldEvents]);

  const { currentStockKg, totalCurrentStockKg } = useMemo(() => {
    const stockByType: Record<string, number> = {};
    let total = 0;
    for (const fs of feedStockData?.feedStock ?? []) {
      stockByType[fs.type] = (stockByType[fs.type] ?? 0) + fs.amount;
      total += fs.amount;
    }
    return { currentStockKg: stockByType, totalCurrentStockKg: total };
  }, [feedStockData]);

  // Actual demand from current stock classes: sum(consumptionKgPerDay × count) / total area
  const actualDemandKgHaDay = useMemo(() => {
    const totalDemandKgDay = stockClasses.reduce(
      (sum, sc) => sum + sc.consumptionKgPerDay * sc.animalCount,
      0
    );
    // Total grazing area from fields assigned to animal groups
    const totalAreaHa = animalGroups.reduce((sum, ag) => {
      const groupFields = getAllFieldsForAnimalGroupIdFromArray(
        allFields,
        ag.guid
      );
      return sum + groupFields.reduce((s, f) => s + (f.area || 0) / 10000, 0);
    }, 0);

    return totalAreaHa > 0 ? totalDemandKgDay / totalAreaHa : 0;
  }, [stockClasses, animalGroups, allFields]);

  return {
    monthlyActuals,
    actualGrowthRatesKgHaDay,
    actualDemandKgHaDay,
    currentStockKg,
    totalCurrentStockKg,
    isLoading: feedLoading || purchaseLoading || yieldLoading || stockLoading,
    currentMonth,
  };
}

export interface FeedStoreProjectionRow {
  month: number;
  plannedConsumptionKg: number;
  plannedHarvestKg: number;
  projectedStockKg: number;
  actualConsumptionKg: number | null;
  actualHarvestKg: number | null;
  actualStockKg: number | null;
}

export function computeFeedStoreProjection(
  currentStockKg: number,
  currentMonth: number,
  plannedDeficitKgHaDay: number[],
  plannedGrazingAreaHa: number[],
  monthlyActuals: MonthlyActual[]
): FeedStoreProjectionRow[] {
  let projectedStock = currentStockKg;
  let actualStock = currentStockKg;
  const rows: FeedStoreProjectionRow[] = [];

  for (let month = 0; month < 12; month++) {
    const grazingDays = DEFAULT_GRAZING_DAYS[month];
    const area = plannedGrazingAreaHa[month];

    const deficit = plannedDeficitKgHaDay[month];
    const plannedConsumptionKg =
      deficit < 0 ? Math.abs(deficit) * area * grazingDays : 0;
    const plannedHarvestKg =
      deficit > 0 ? deficit * area * grazingDays * 0.5 : 0;

    projectedStock = projectedStock - plannedConsumptionKg + plannedHarvestKg;

    const actual = monthlyActuals[month];
    const isPast = month <= currentMonth;

    let actualConsumptionKg: number | null = null;
    let actualHarvestKg: number | null = null;
    let actualStockAtEnd: number | null = null;

    if (isPast) {
      actualConsumptionKg = actual.feedConsumedKg;
      actualHarvestKg = actual.forageHarvestedKeptKg;
      actualStock =
        actualStock -
        actual.feedConsumedKg +
        actual.forageHarvestedKeptKg +
        actual.feedPurchasedKg;
      actualStockAtEnd = actualStock;
    }

    rows.push({
      month,
      plannedConsumptionKg,
      plannedHarvestKg,
      projectedStockKg: Math.max(0, projectedStock),
      actualConsumptionKg,
      actualHarvestKg,
      actualStockKg:
        actualStockAtEnd !== null ? Math.max(0, actualStockAtEnd) : null,
    });
  }

  return rows;
}
